import EditDocumentMetadataDialog from './EditDocumentMetadataDialog';
import { getDocumentDueDate } from '../../utils/documents.overview';
import DocumentNotesPanel from './DocumentNotesPanel';
import React, { useMemo, useState } from 'react';
import { Building2, Calendar, CalendarClock, ChevronRight, Euro, FileText, MapPin, User } from '../../../../shared/assets/AppIcons';
import EntityDetailDialog from '@/shared/components/EntityDetailDialog';
import RowActions from '../../../../shared/components/patterns/RowActions';
import StatusPill from '../../../../shared/components/patterns/StatusPill';
import { getDocumentStatusView } from './documentsPage.config';
import { formatDocumentDate } from '../../utils';
import { getDocumentFileExtension } from '../../utils/documents.helpers';
import EntityLinkButton from '../../../../shared/components/linked-entities/EntityLinkButton';
import { entityLabel } from '../../../../shared/utils/entitySelection';
import '../../styles/documents.css';

import { useLinkedEntityCards } from '../../../../shared/components/linked-entities/LinkedEntityCardHost';
import ClientCustomFieldsSection from '../../../clients/components/ClientCustomFieldsSection';
import {
  archiveDocumentsCustomField,
  createDocumentsCustomField,
  listDocumentsCustomFields,
} from '../../services/documents-custom-fields.service';

const DOCUMENT_CUSTOM_FIELDS_API = {
  list: listDocumentsCustomFields,
  create: createDocumentsCustomField,
  archive: archiveDocumentsCustomField,
};

function documentCustomFieldsFormKey(document) {
  const category = String(document?.category || document?.type || '').toLowerCase();
  if (category.includes('mandat')) return 'documents.mandate.create';
  if (category.includes('contrat')) return 'documents.contract.create';
  return '';
}

function DetailSection({ title, children }) {
  return <section className="document-detail__section" aria-label={title}>
    <h3>{title}</h3>{children}
  </section>;
}

function DetailField({ label, icon: Icon, children }) {
  return <div className="document-detail__field">
    <span className="document-detail__field-icon" aria-hidden="true"><Icon size={24} /></span>
    <div><dt>{label}</dt><dd className="select-text">{children}</dd></div>
  </div>;
}

function displayDate(value) {
  return value && Number.isFinite(new Date(value).getTime()) ? formatDocumentDate(value) : 'Non impostata';
}

function displayValue(value) {
  if (value === null || value === undefined || value === '') return 'Non indicato';
  // Keep legacy formatted amounts intact; format only numeric values.
  return Number.isFinite(Number(value))
    ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(Number(value))
    : String(value);
}

function displaySize(size) {
  if (typeof size === 'number' && Number.isFinite(size)) {
    return size < 1024 ? `${size} B` : size < 1024 * 1024 ? `${(size / 1024).toFixed(1)} KB` : `${(size / 1024 / 1024).toFixed(2)} MB`;
  }
  return size && size !== 'N/D' ? String(size) : '';
}

const ORIGIN_MODULE_LABELS = {
  deals: 'Affari',
  assignments: 'Task',
  task: 'Task',
  accounting: 'Contabilità',
  documents: 'Documenti',
};

function getDocumentOrigin(document) {
  const moduleKey = String(document?.linkedModule || document?.sourceModule || '')
    .trim()
    .toLowerCase();
  const moduleLabel = ORIGIN_MODULE_LABELS[moduleKey] || (moduleKey ? moduleKey : 'Documenti');
  const entityLabel =
    document?.linkedEntityLabel ||
    document?.sourceEntityLabel ||
    document?.originLabel ||
    (document?.assignmentId ? document.assignmentTitle || `Task ${document.assignmentId}` : '') ||
    (document?.linkedEntityId ? `${document.linkedEntityType || 'Entità'} ${document.linkedEntityId}` : '') ||
    'Caricamento manuale';
  return `${moduleLabel} · ${entityLabel}`;
}

export default function DocumentDetailDialog({
  document,
  isOpen,
  onClose,
  onEdit,
  onSave,
  onDownload,
  onDelete,
  canManageDocuments = false,
  focusComments = false,
  clients = [],
  staff = [],
}) {
  const { openEntityCard } = useLinkedEntityCards();
  const [editing, setEditing] = useState(false);
  React.useEffect(() => { setEditing(false); }, [document?.id, isOpen]);
  const statusView = getDocumentStatusView(document?.status);
  const clientName = document?.clientResolvedName || document?.client || 'Cliente non risolto';
  const origin = getDocumentOrigin(document);
  const relations = useMemo(() => {
    const items = [];
    const configs = {
      CLIENT: ['Cliente', 'client'],
      SUPPLIER: ['Fornitore', 'supplier'],
      DEAL: ['Affare', 'inProgressDeal'],
      ASSIGNMENT: ['Task', 'assignment'],
      TEAM_MEMBER: ['Team / Personale', 'staff'],
      PAYMENT: ['Incasso', 'payment'],
      INVOICE: ['Fattura', 'invoice'],
      EXPENSE: ['Spesa', 'expense'],
      ACCOUNTING_PAYMENT: ['Incasso', 'payment'],
      ACCOUNTING_INVOICE: ['Fattura', 'invoice'],
      ACCOUNTING_EXPENSE: ['Spesa', 'expense'],
      COMPLETED_DEAL: ['Affare completato', 'completedDeal'],
    };
    const canonical = [
      ...(document?.relations || []),
      ...(document?.clientId ? [{ entityType: 'CLIENT', entityId: document.clientId, label: clientName }] : []),
      ...(document?.assignmentId ? [{ entityType: 'ASSIGNMENT', entityId: document.assignmentId, label: document.assignmentTitle }] : []),
      ...(document?.linkedEntityId ? [{ entityType: document.linkedEntityType, entityId: document.linkedEntityId, label: document.linkedEntityLabel }] : []),
    ];
    canonical.forEach((relation) => {
      const type = String(relation?.entityType || '').toUpperCase();
      const id = relation?.entityId;
      const config = configs[type];
      if (!id || !config) return;
      const [label, entityType] = config;
      const key = `${entityType}:${id}`;
      const catalog = entityType === 'client' ? clients : entityType === 'staff' ? staff : [];
      const resolvedName = entityLabel(catalog.find(item => String(item.id) === String(id)));
      const legacyName = entityType === 'client' && String(id) === String(document.clientId) ? clientName : '';
      if (!items.some((item) => item.key === key))
        items.push({ key, label, name: resolvedName || relation.label || legacyName || `${label} ${id}`, entityType, entityId: id });
    });
    return items;
  }, [clientName, document, clients, staff]);

  if (!document) return null;

  const linkedClients = relations.filter(item => item.entityType === 'client');
  const teamMembers = relations.filter(item => item.entityType === 'staff');
  const legacyTeam = document.staff || document.team || document.personaleCoinvolto || [];
  (Array.isArray(legacyTeam) ? legacyTeam : []).forEach(member => {
    const memberId = typeof member === 'object' && member ? member.id || member.staffId || member.userId : null;
    const name = typeof member === 'object' && member ? entityLabel(member) || member.label : String(member || '');
    if (name && !teamMembers.some(item => memberId ? String(item.entityId) === String(memberId) : item.name === name)) {
      teamMembers.push({ key: memberId || name, name, entityType: 'staff', entityId: memberId });
    }
  });
  const otherRelations = relations.filter(item => !['client', 'staff'].includes(item.entityType));
  const fileName = document.originalFilename || document.fileName || '';
  const fileFormat = getDocumentFileExtension({ fileName })?.toUpperCase() || (document.mimeType === 'application/pdf' ? 'PDF' : 'FILE');
  const category = document.category || document.type || 'Documento';
  const internalNotes = document.notes || document.note;
  const counterparty = document.counterparty || document.company;
  const renderParties = (items, fallback) => <div className="document-detail__party-links">
    {items.length ? items.map(item => item.entityId
      ? <EntityLinkButton key={item.key} aria-label={`Apri ${item.name}`} onClick={() => openEntityCard(item)}>{item.name}</EntityLinkButton>
      : <span key={item.key}>{item.name}</span>) : fallback}
  </div>;

  const ordinaryActions = onDownload ? (
    <RowActions
      actions={[
        {
          id: 'download',
          iconKey: 'actions.download',
          label: 'Scarica documento',
          onClick: () => onDownload(document),
        },
      ]}
    />
  ) : null;
  const editActions = onSave || onEdit ? <RowActions actions={[{ id: 'edit', iconKey: 'actions.edit',
    label: 'Modifica documento', onClick: () => onSave ? setEditing(true) : onEdit(document) }]} /> : null;
  const dangerActions =
    canManageDocuments && onDelete ? (
      <RowActions
        actions={[
          {
            id: 'delete',
            iconKey: 'actions.delete',
            label: 'Elimina documento',
            onClick: () => onDelete(document),
            tone: 'danger',
          },
        ]}
      />
    ) : null;

  if (editing) return <EditDocumentMetadataDialog open={isOpen} document={document}
    onOpenChange={next => { if (!next) setEditing(false); }} onSubmit={onSave} />;

  return (
    <EntityDetailDialog
      isOpen={isOpen}
      onClose={onClose}
      className="document-detail"
      avatarLabel={<FileText size={32} />}
      title={document.name || document.title || fileName || 'Documento'}
      headerMeta={<div className="document-detail__header-meta">
        <span className="entity-detail-dialog__badge is-active">{category}</span>
        {fileName ? <span className="document-detail__file-state"><span aria-hidden="true" />Documento caricato</span> : null}
        <StatusPill label={statusView.label} tone={statusView.tone} />
      </div>}
      actions={ordinaryActions}
      editActions={editActions}
      dangerActions={dangerActions}
    >
      <div className="document-detail__layout">
        <DetailSection title="Descrizione">
          <p className="document-detail__description select-text">{document.description || 'Nessuna descrizione'}</p>
        </DetailSection>

        <DetailSection title="File">
          <div className="document-detail__file">
            <span className="document-detail__file-format" aria-hidden="true">{fileFormat}</span>
            <div className="document-detail__file-info">
              <strong className="select-text">{fileName || 'Nessun file associato'}</strong>
              {fileName ? <p>{[fileFormat === 'FILE' ? 'Documento' : `Documento ${fileFormat}`, displaySize(document.sizeBytes ?? document.size)].filter(Boolean).join(' · ')}</p> : null}
            </div>
            {ordinaryActions}
          </div>
        </DetailSection>

        <DetailSection title="Dettagli documento">
          <dl className="document-detail__fields">
            <DetailField label="Cliente" icon={Building2}>{renderParties(linkedClients, clientName)}</DetailField>
            <DetailField label="Personale" icon={User}>{renderParties(teamMembers, 'Nessun personale indicato')}</DetailField>
            <DetailField label="Data documento" icon={Calendar}>{displayDate(document.documentDate || document.uploadDate || document.createdAt)}</DetailField>
            <DetailField label="Scadenza" icon={CalendarClock}>{displayDate(getDocumentDueDate(document))}</DetailField>
            <DetailField label="Valore" icon={Euro}>{displayValue(document.value ?? document.amount)}</DetailField>
            <DetailField label="Origine" icon={MapPin}>
              {document.linkedEntityId || document.assignmentId ? <EntityLinkButton onClick={() => openEntityCard({
                entityType: document.linkedEntityId ? document.linkedEntityType : 'assignment',
                entityId: document.linkedEntityId || document.assignmentId,
              })}>{origin}</EntityLinkButton> : origin}
            </DetailField>
          </dl>
        </DetailSection>

        {otherRelations.length ? <DetailSection title="Collegamenti">
          <div className="document-detail__relations">
            {otherRelations.map(relation => <EntityLinkButton key={relation.key} className="document-detail__relation"
              aria-label={`Apri ${relation.label}: ${relation.name}`} onClick={() => openEntityCard(relation)}>
              <span><small>{relation.label}</small><strong>{relation.name}</strong></span><ChevronRight size={20} />
            </EntityLinkButton>)}
          </div>
        </DetailSection> : null}

        {counterparty || document.uploadedBy ? <DetailSection title="Informazioni aggiuntive">
          <dl className="document-detail__fields">
            {counterparty ? <DetailField label="Controparte" icon={Building2}>{counterparty}</DetailField> : null}
            {document.uploadedBy ? <DetailField label="Gestito da" icon={User}>{document.uploadedBy}</DetailField> : null}
          </dl>
        </DetailSection> : null}
        {internalNotes ? <DetailSection title="Descrizione interna"><p className="document-detail__description select-text">{internalNotes}</p></DetailSection> : null}
        {documentCustomFieldsFormKey(document) ? <ClientCustomFieldsSection readOnly
          formKey={documentCustomFieldsFormKey(document)} values={document.customFields || {}} api={DOCUMENT_CUSTOM_FIELDS_API} /> : null}
        <section aria-label="Commenti" className="document-detail__comments">
          <DocumentNotesPanel key={document.id} documentId={document.id} open={isOpen} autoFocus={focusComments} />
        </section>
      </div>
    </EntityDetailDialog>
  );
}
